import classNames from 'classnames';
import { memo, useEffect, useMemo, useState } from 'react';
import CurrencyInput from 'react-currency-input';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { ButtonGroup, Checkbox, CheckboxGroup, Dropdown, InputGroup, InputNumber } from 'rsuite';
import { RootState } from '../../../../../@types/RootState';
import {
  ChangedCostsFilter,
  initialChangedCostsDays,
  selectorChangedCosts,
  setChangedCostsFilter,
  setChangedCostsIsActive,
  toggleChangedCostsIsActive,
} from '../../../../../reducers/gerenciadorPrecos/quickActionsFilter';
import { BaseQuickActionFilterProps, IconButton } from '../QuickActionFilter';
import quickActionFilterStyles from '../QuickActionFilter/elements/FilterPriceVariation.module.scss';
import styles from './NewCostsFilter.module.scss';
import {
  buildChangedCostsFilter,
  clampMinPct,
  DIRECTION_OPTIONS,
  DirectionOption,
  getDirectionValues,
  getNextDirections,
  isLastCheckedDirection,
  MAX_CHANGED_COSTS_DAYS,
} from './NewCostsFilter.utils';

type NewCostsFilterProps = RouteComponentProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const NewCostsFilter = memo(({ changedCosts, setChangedCostsFilter, setChangedCostsIsActive, toggleChangedCostsIsActive }: NewCostsFilterProps) => {
  const [innerValue, setInnerValue] = useState<number | string>(changedCosts.value ?? initialChangedCostsDays);
  // `react-currency-input` guarda o valor mascarado em estado interno e só ressincroniza quando o pai
  // renderiza. Guardar o percentual em um objeto garante identidade nova a cada digitação, então o input
  // volta para o valor já limitado por `clampMinPct` mesmo quando o número não muda.
  const [minPct, setMinPct] = useState(() => ({ value: clampMinPct(changedCosts.minPct) }));
  const [innerDirections, setInnerDirections] = useState<DirectionOption[]>(getDirectionValues(changedCosts));

  useEffect(() => {
    if (typeof changedCosts.value === 'number') {
      setInnerValue(changedCosts.value);
    }
    setMinPct({ value: clampMinPct(changedCosts.minPct) });
    setInnerDirections(getDirectionValues(changedCosts));
  }, [changedCosts]);

  const skinColor = useMemo<BaseQuickActionFilterProps['skin']>(() => {
    return changedCosts.isActive ? 'primary' : 'neutral';
  }, [changedCosts.isActive]);

  const buildFilter = (isActive: boolean) => buildChangedCostsFilter({ days: innerValue, minPct: minPct.value, directions: innerDirections, isActive });

  const handleClickToggle = () => {
    setChangedCostsFilter(buildFilter(changedCosts.isActive));
    toggleChangedCostsIsActive();
  };

  const handleChangeValue = (value: number | string) => {
    setInnerValue(value);
  };

  const handleChangeMinPct = (value: number) => {
    setMinPct({ value: clampMinPct(value) });
  };

  const handleChangeDirections = (value: DirectionOption[]) => {
    setInnerDirections(current => getNextDirections(current, value));
  };

  const handleCloseDropdown = () => {
    setChangedCostsFilter(buildFilter(true));
    setChangedCostsIsActive(true);
  };

  return (
    <ButtonGroup className={classNames('quick-action-filter__button__container quick-action-filter__toggle', quickActionFilterStyles.container)}>
      <IconButton icon="MdSwapVerticalCircle" skin={skinColor} onClick={handleClickToggle} placeholder="Filtrar custos alterados" />
      <Dropdown
        activeKey="1"
        eventKey="1"
        placement="bottomEnd"
        renderTitle={() => <IconButton icon="MdKeyboardArrowDown" className="quick-action-filter__toggle__arrow" skin={skinColor} />}
        onClose={handleCloseDropdown}
        menuStyle={{ width: '282px' }}
      >
        <Dropdown.Item panel className="quick-action-filter__select-interval" style={{ ['--select-interval-padding' as string]: '15px 12px 14px' }}>
          <div className={styles.panel}>
            <div className={'quick-action-filter__select-interval__dropdown-item'}>
              <p>Filtrar custos alterados nos últimos</p>
              <InputNumber min={0} max={MAX_CHANGED_COSTS_DAYS} value={innerValue} onChange={handleChangeValue} />
              <p>dias</p>
            </div>

            <p className={styles.label}>Com variação mínima de</p>
            <InputGroup className={styles.percentGroup}>
              <CurrencyInput
                className={styles.percentInput}
                value={minPct.value}
                onChangeEvent={(_e: unknown, _masked: unknown, value: number) => handleChangeMinPct(value)}
                allowNegative={false}
                precision="1"
                decimalSeparator=","
                thousandSeparator="."
              />
              <InputGroup.Addon>%</InputGroup.Addon>
            </InputGroup>

            <hr className={styles.divider} />

            <CheckboxGroup
              className={styles.directionGroup}
              name="changedCostsDirection"
              value={innerDirections}
              onChange={value => handleChangeDirections(value as DirectionOption[])}
            >
              {DIRECTION_OPTIONS.map(option => (
                <Checkbox
                  key={option.value}
                  value={option.value}
                  disabled={isLastCheckedDirection(innerDirections, option.value)}
                  title={isLastCheckedDirection(innerDirections, option.value) ? 'Selecione ao menos uma direção' : undefined}
                >
                  {option.label}
                </Checkbox>
              ))}
            </CheckboxGroup>
          </div>
        </Dropdown.Item>
      </Dropdown>
    </ButtonGroup>
  );
});

NewCostsFilter.displayName = 'NewCostsFilter';

const mapStateToProps = (state: RootState) => ({
  changedCosts: selectorChangedCosts(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setChangedCostsFilter: (payload: ChangedCostsFilter) => dispatch(setChangedCostsFilter(payload)),
  toggleChangedCostsIsActive: () => dispatch(toggleChangedCostsIsActive()),
  setChangedCostsIsActive: (payload: boolean) => dispatch(setChangedCostsIsActive(payload)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(NewCostsFilter));
